import moment from "moment";
import { AnalyticsGranularity, MetricRate } from "../../../../app/data/invoice-factoring/models";

export const formatPercent = (rate: MetricRate): string => {
  if (!rate.denominator) return "—";
  const percent = (rate.numerator / rate.denominator) * 100;
  return `${Number.isInteger(percent) ? percent : percent.toFixed(1)}%`;
};

/** Always shows the denominator, e.g. "3 of 4 (75%)". */
export const formatRate = (rate: MetricRate): string =>
  rate.denominator ? `${rate.numerator} of ${rate.denominator} (${formatPercent(rate)})` : `${rate.numerator} of 0`;

/** 0 to 100, for bar widths; 0 when there is no denominator. */
export const ratePercent = (rate: MetricRate): number =>
  rate.denominator ? Math.min(100, Math.max(0, (rate.numerator / rate.denominator) * 100)) : 0;

/** Short dollar labels for chart axes, e.g. "$950", "$2.3k", "$1.2M". */
export const formatCompactCents = (cents: number | string): string => {
  const dollars = Number(cents) / 100;
  const abs = Math.abs(dollars);
  if (abs >= 1000000) return `$${(dollars / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1000) return `$${(dollars / 1000).toFixed(abs >= 10000 ? 0 : 1).replace(/\.0$/, "")}k`;
  return `$${Math.round(dollars)}`;
};

export const formatPayor = (payorName?: string | null, payorNumber?: number | null): string => {
  if (!payorName && !payorNumber) return "Unknown payor";
  if (!payorNumber) return payorName as string;
  return payorName ? `${payorName} (${payorNumber})` : `Payor ${payorNumber}`;
};

export const formatDuration = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours.toFixed(1).replace(/\.0$/, "")}h`;
  return `${(hours / 24).toFixed(1).replace(/\.0$/, "")}d`;
};

/** Periods are UTC period starts from the API. */
export const formatPeriod = (period: string, granularity: AnalyticsGranularity): string => {
  const start = moment.utc(period);
  if (granularity === "month") return start.format("MMM YYYY");
  if (granularity === "week") return `Week of ${start.format("MM/DD")}`;
  return start.format("MM/DD");
};

/** Turns an inclusive UI date range (MM/DD/YYYY, local time) into the API's [from, to) ISO range. */
export const toApiRange = (start: string, end: string, now: Date = new Date()): { from: string; to: string } => {
  const from = moment(start, "MM/DD/YYYY", true).startOf("day");
  const endExclusive = moment(end, "MM/DD/YYYY", true).add(1, "day").startOf("day");
  const to = moment.min(endExclusive, moment(now));
  return { from: from.toISOString(), to: (to.isAfter(from) ? to : endExclusive).toISOString() };
};
